import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Megaphone, Plus, Archive, RotateCcw, Trash2, Loader2, AlertOctagon, Check, Users, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TARGET_ROLES = [
  { value: "field", label: "Field workers" },
  { value: "mechanic", label: "Mechanics" },
  { value: "office", label: "Office (incl. yourself)" },
  { value: "admin", label: "Admins" },
];

export default function Announcements() {
  const { user } = useAuth();
  const isOffice = user.role !== "field" && user.role !== "mechanic";
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("active");
  const [newOpen, setNewOpen] = useState(false);
  const [acksFor, setAcksFor] = useState(null); // announcement object

  const load = async () => {
    try {
      const archived = tab === "archived" ? true : tab === "active" ? false : null;
      const url = archived == null ? "/announcements" : `/announcements?archived=${archived}`;
      const { data } = await api.get(url);
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, [tab]);
  useEffect(() => { if (params.get("new") === "1" && isOffice) setNewOpen(true); }, [params, isOffice]);

  const counts = useMemo(() => {
    const active = items.filter((a) => a.active).length;
    const archived = items.filter((a) => !a.active).length;
    return { active, archived };
  }, [items]);

  const archive = async (a) => {
    if (!window.confirm(`Archive “${a.title}”? Crew won't see it any more, but the read-receipts stay on file.`)) return;
    try { await api.post(`/announcements/${a.id}/archive`); toast.success("Archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };
  const restore = async (a) => {
    try { await api.post(`/announcements/${a.id}/restore`); toast.success("Restored — crew will see it again"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete “${a.title}” permanently? All read-receipts will also be wiped.`)) return;
    try { await api.delete(`/announcements/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="announcements-page" className="p-4 lg:p-10 max-w-4xl mx-auto">
      <PageHeader
        tag="/ notice board"
        title="Notice board"
        subtitle={isOffice
          ? "Post a message and your crew sees it as the first thing they see until they tap GOT IT. You'll see exactly who has read it."
          : "Notices posted by the office. You'll see new ones as a blocker until you acknowledge them."}
        action={isOffice && (
          <Button
            data-testid="announcement-new-btn"
            onClick={() => setNewOpen(true)}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Post notice
          </Button>
        )}
      />

      {isOffice && (
        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList className="rounded-sm bg-zinc-100 dark:bg-[#151C27] p-1 h-auto">
            <TabsTrigger value="active" data-testid="ann-tab-active" className="rounded-sm text-[12px] font-semibold">
              Active <span className="ml-1.5 text-zinc-500">{counts.active}</span>
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="ann-tab-archived" className="rounded-sm text-[12px] font-semibold">
              Archived <span className="ml-1.5 text-zinc-500">{counts.archived}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {!loaded ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={tab === "archived" ? "Nothing archived" : "No notices yet"}
          description={isOffice ? "Tap “Post notice” to send a message that will block your crew until they acknowledge." : "Office hasn't posted anything yet."}
        />
      ) : (
        <div className="space-y-2.5" data-testid="announcements-list">
          {items.map((a) => (
            <AnnouncementCard
              key={a.id}
              ann={a}
              isOffice={isOffice}
              onArchive={() => archive(a)}
              onRestore={() => restore(a)}
              onDelete={() => del(a)}
              onViewAcks={() => setAcksFor(a)}
            />
          ))}
        </div>
      )}

      {isOffice && <NewAnnouncementDialog open={newOpen} onOpenChange={setNewOpen} onPosted={load} />}
      <AcksDialog ann={acksFor} onClose={() => setAcksFor(null)} />
    </div>
  );
}

function AnnouncementCard({ ann, isOffice, onArchive, onRestore, onDelete, onViewAcks }) {
  const urgent = ann.priority === "urgent";
  const ackPct = ann.target_count > 0 ? Math.round((ann.ack_count / ann.target_count) * 100) : 0;
  return (
    <div
      data-testid={`announcement-${ann.id}`}
      className={`hard-card p-4 transition-colors ${
        urgent ? "border-red-300 dark:border-red-800 bg-red-50/30 dark:bg-red-950/30"
              : "hover:border-[#FF5F15]"
      } ${!ann.active ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 flex items-center justify-center shrink-0 rounded-sm ${
          urgent ? "bg-red-600/10 text-red-600 dark:text-red-400" : "bg-[#FF5F15]/10 text-[#FF5F15]"
        }`}>
          {urgent ? <AlertOctagon className="w-5 h-5" /> : <Megaphone className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[15px] truncate">{ann.title}</span>
                {urgent && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-red-600 text-white">
                    Urgent
                  </span>
                )}
                {!ann.active && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                    Archived
                  </span>
                )}
              </div>
              <div className="text-[11.5px] text-zinc-500 dark:text-zinc-400 mt-0.5 font-mono uppercase tracking-widest">
                {ann.created_by_name} · {(ann.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
            {isOffice && (
              <div className="text-right">
                <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Acknowledged</div>
                <div className="text-[15px] font-bold tabular-nums">
                  <span className={ann.pending_count === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}>
                    {ann.ack_count}
                  </span>
                  <span className="text-zinc-400"> / {ann.target_count}</span>
                  <span className="ml-1.5 text-[11px] text-zinc-500">({ackPct}%)</span>
                </div>
              </div>
            )}
          </div>

          <div className="text-[13.5px] text-zinc-700 dark:text-zinc-300 mt-2.5 whitespace-pre-wrap leading-relaxed">
            {ann.body}
          </div>

          {!isOffice && ann.acknowledged && (
            <div className="mt-3 text-[11.5px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Acknowledged {(ann.acknowledged_at || "").slice(0, 16).replace("T", " ")}
            </div>
          )}

          {isOffice && (
            <div className="flex flex-wrap items-center gap-2 mt-3.5">
              <Button size="sm" variant="outline" onClick={onViewAcks} data-testid={`ann-acks-${ann.id}`} className="rounded-sm">
                <Users className="w-3.5 h-3.5 mr-1.5" /> Who&apos;s seen it
              </Button>
              {ann.active ? (
                <Button size="sm" variant="outline" onClick={onArchive} data-testid={`ann-archive-${ann.id}`} className="rounded-sm">
                  <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={onRestore} data-testid={`ann-restore-${ann.id}`} className="rounded-sm">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`ann-delete-${ann.id}`}
                className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 ml-auto">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewAnnouncementDialog({ open, onOpenChange, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [targetRoles, setTargetRoles] = useState(["field"]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setTitle(""); setBody(""); setPriority("normal"); setTargetRoles(["field"]); }
  }, [open]);

  const toggleRole = (r) => {
    setTargetRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!body.trim()) { toast.error("Message body is required"); return; }
    if (targetRoles.length === 0) { toast.error("Pick at least one role to notify"); return; }
    setSaving(true);
    try {
      await api.post("/announcements", { title: title.trim(), body: body.trim(), priority, target_roles: targetRoles });
      toast.success("Notice posted — crew will see it on next app open");
      onPosted(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ann-new-dialog">
        <DialogHeader>
          <DialogTitle>Post a notice</DialogTitle>
          <DialogDescription>
            This will be the first thing your crew sees on app open. They can&apos;t dismiss it until they tap GOT IT.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Title</Label>
            <Input
              data-testid="ann-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Site closed Friday — Cyclone warning"
              className="rounded-sm mt-1"
              maxLength={120}
            />
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Message</Label>
            <Textarea
              data-testid="ann-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write it like you'd say it on the radio. Keep it short and clear."
              className="rounded-sm mt-1 min-h-32"
              maxLength={4000}
            />
            <div className="text-[10px] font-mono text-zinc-500 text-right mt-1">{body.length} / 4000</div>
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Priority</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {["normal", "urgent"].map((p) => (
                <button
                  type="button"
                  key={p}
                  data-testid={`ann-priority-${p}`}
                  onClick={() => setPriority(p)}
                  className={`p-2.5 rounded-sm border-2 text-[12px] font-bold uppercase tracking-wider transition-all ${
                    priority === p
                      ? (p === "urgent"
                          ? "border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                          : "border-[#FF5F15] bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300")
                      : "border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-400"
                  }`}
                >
                  {p === "urgent" ? "⚠ Urgent (red)" : "Normal"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Who must acknowledge</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {TARGET_ROLES.map((r) => (
                <label
                  key={r.value}
                  className="flex items-center gap-2 p-2.5 rounded-sm border border-zinc-200 dark:border-zinc-800 cursor-pointer hover:border-[#FF5F15]"
                  data-testid={`ann-target-${r.value}`}
                >
                  <Checkbox
                    checked={targetRoles.includes(r.value)}
                    onCheckedChange={() => toggleRole(r.value)}
                  />
                  <span className="text-[13px] font-semibold">{r.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ann-submit" className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Megaphone className="w-4 h-4 mr-1.5" />}
            Post notice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcksDialog({ ann, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!ann) { setData(null); return; }
    api.get(`/announcements/${ann.id}/acks`).then((r) => setData(r.data)).catch(() => setData({ acknowledged: [], pending: [] }));
  }, [ann]);

  return (
    <Dialog open={!!ann} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="ann-acks-dialog">
        <DialogHeader>
          <DialogTitle>Read receipts</DialogTitle>
          <DialogDescription>{ann?.title}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex items-center justify-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-4 text-[13px]">
            <Section title={`Acknowledged · ${data.acknowledged.length}`} tone="ok">
              {data.acknowledged.length === 0 && <div className="text-zinc-500 text-[12px]">Nobody yet.</div>}
              {data.acknowledged.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-1.5 text-[13px] border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                  <div>
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">{u.role}</div>
                  </div>
                  <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-mono">
                    {(u.acknowledged_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              ))}
            </Section>
            <Section title={`Still pending · ${data.pending.length}`} tone="warn">
              {data.pending.length === 0 && <div className="text-zinc-500 text-[12px]">Everyone has acknowledged. 🎯</div>}
              {data.pending.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-1.5 text-[13px] border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                  <div>
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">{u.role}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />
                </div>
              ))}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, tone, children }) {
  const cls = tone === "ok"
    ? "border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300"
    : "border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300";
  return (
    <div>
      <div className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5 pb-1.5 border-b ${cls}`}>
        <span className="opacity-50">//</span> {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
